import { Fetcher } from "../fetcher";
import { ApiSingleResponse, ApiCollectionResponse } from "../types/api";
import type { SessionDTO, CompanySessionDTO } from "../sessions/types";
import {
  PlatformCompany,
  PlatformUser,
  Role,
  Permission,
  CompanyMetrics,
  DashboardStats,
  CompanyUserDetailed,
  PlatformDepartment,
  PlatformCurrency,
  PlatformContact,
  PlatformShipment,
  PlatformVehicle,
  PlatformDriver,
  PlatformVendor,
  PlatformCustomer,
  CompanyFilters,
  UserFilters,
  RoleFilters,
  PermissionFilters,
} from "../entities/platform";
import {
  CreatePlatformCompanyDto,
  UpdatePlatformCompanyDto,
  CreatePlatformUserDto,
  UpdatePlatformUserDto,
  CreatePlatformDepartmentDto,
  UpdatePlatformDepartmentDto,
  Broadcast,
  BroadcastAudienceCounts,
  SendBroadcastDto,
} from "./types";

export function createPlatformRepository(fetcher: Fetcher) {
  const buildParams = (filters: any) => {
    const params = new URLSearchParams();
    if (!filters) return "";
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });
    return params.toString();
  };

  return {
    // ===================== COMPANIES =====================
    async listCompanies(filters: CompanyFilters = {}): Promise<ApiCollectionResponse<PlatformCompany>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformCompany>>(`/platform/organizations?${query}`);
    },

    async getCompanyById(id: string): Promise<ApiSingleResponse<PlatformCompany>> {
      return fetcher<ApiSingleResponse<PlatformCompany>>(`/platform/organizations/${id}`);
    },

    async createCompany(payload: CreatePlatformCompanyDto): Promise<ApiSingleResponse<PlatformCompany>> {
      return fetcher<ApiSingleResponse<PlatformCompany>>("/platform/organizations", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    async updateCompany(id: string, payload: UpdatePlatformCompanyDto): Promise<ApiSingleResponse<PlatformCompany>> {
      return fetcher<ApiSingleResponse<PlatformCompany>>(`/platform/organizations/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    },

    async activateCompany(id: string): Promise<ApiSingleResponse<PlatformCompany>> {
      return fetcher<ApiSingleResponse<PlatformCompany>>(`/platform/organizations/${id}/activate`, {
        method: "POST",
      });
    },

    async deactivateCompany(id: string): Promise<ApiSingleResponse<PlatformCompany>> {
      return fetcher<ApiSingleResponse<PlatformCompany>>(`/platform/organizations/${id}/deactivate`, {
        method: "POST",
      });
    },

    async getCompanyMetrics(id: string): Promise<ApiSingleResponse<CompanyMetrics>> {
      return fetcher<ApiSingleResponse<CompanyMetrics>>(`/platform/organizations/${id}/metrics`);
    },

    // ===================== COMPANY SUB-RESOURCES =====================
    async listCompanyDepartments(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformDepartment>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformDepartment>>(`/platform/organizations/${companyId}/departments?${query}`);
    },

    async createCompanyDepartment(companyId: string, payload: CreatePlatformDepartmentDto): Promise<ApiSingleResponse<PlatformDepartment>> {
      return fetcher<ApiSingleResponse<PlatformDepartment>>(`/platform/organizations/${companyId}/departments`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    async updateCompanyDepartment(companyId: string, id: string, payload: UpdatePlatformDepartmentDto): Promise<ApiSingleResponse<PlatformDepartment>> {
      return fetcher<ApiSingleResponse<PlatformDepartment>>(`/platform/organizations/${companyId}/departments/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    },

    async deleteCompanyDepartment(companyId: string, id: string): Promise<void> {
      await fetcher(`/platform/organizations/${companyId}/departments/${id}`, {
        method: "DELETE",
      });
    },

    async listCompanyUsers(companyId: string, filters: UserFilters = {}): Promise<ApiCollectionResponse<CompanyUserDetailed>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<CompanyUserDetailed>>(`/platform/organizations/${companyId}/users?${query}`);
    },

    /**
     * Create a CompanyUser inside the given organization. The route is
     * `/platform/auth/company/register` rather than nested under the org
     * because that's where the auth service lives — the new user becomes a
     * full login principal for the tenant, not just a sub-resource record.
     */
    async registerCompanyUser(payload: {
      name: string;
      email: string;
      password: string;
      companyId: string;
      departmentId?: string;
    }): Promise<ApiSingleResponse<CompanyUserDetailed>> {
      return fetcher<ApiSingleResponse<CompanyUserDetailed>>(
        `/platform/auth/company/register`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
    },

    async listCompanyVendors(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformVendor>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformVendor>>(`/platform/organizations/${companyId}/vendors?${query}`);
    },

    async listCompanyCurrencies(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformCurrency>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformCurrency>>(`/platform/organizations/${companyId}/currencies?${query}`);
    },

    async listCompanyContacts(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformContact>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformContact>>(`/platform/organizations/${companyId}/contacts?${query}`);
    },

    async listCompanyCustomers(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformCustomer>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformCustomer>>(`/platform/organizations/${companyId}/customers?${query}`);
    },

    async listCompanyRegions(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/regions?${query}`);
    },

    async listCompanyQuoteSources(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/quote-sources?${query}`);
    },

    async listCompanyClientTypes(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/client-types?${query}`);
    },

    async listCompanyEntityRatings(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/entity-ratings?${query}`);
    },

    async listCompanyRefusalReasons(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/refusal-reasons?${query}`);
    },

    async listCompanyContactPositions(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/contact-positions?${query}`);
    },

    async listCompanyCargos(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/cargos?${query}`);
    },

    async listTenantUsers(filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/tenant-users?${query}`);
    },

    /**
     * Fetch a platform reference profile (e.g. "organization-create"
     * returns `{ countries }`).
     */
    async getReferenceProfile(profile: string): Promise<ApiSingleResponse<any>> {
      return fetcher<ApiSingleResponse<any>>(
        `/platform/reference?profile=${encodeURIComponent(profile)}`,
      );
    },

    /**
     * Google Places autocomplete (proxied through the backend). Returns a
     * normalised `PlaceSuggestion[]` so the client never sees Google
     * directly. Mounted under /platform so admins can use it without a
     * tenant context.
     */
    async getPlacesAutocomplete(params: {
      input: string;
      mode?: "address" | "region";
      language?: string;
      sessiontoken?: string;
    }): Promise<ApiSingleResponse<any>> {
      const query = buildParams(params);
      return fetcher<ApiSingleResponse<any>>(
        `/platform/places/autocomplete?${query}`,
      );
    },

    /**
     * Resolve a Google `placeId` to a normalised address (with
     * countryId already mapped to a Country row).
     */
    async getPlaceDetails(placeId: string): Promise<ApiSingleResponse<any>> {
      return fetcher<ApiSingleResponse<any>>(
        `/platform/places/details?placeId=${encodeURIComponent(placeId)}`,
      );
    },

    async listCompanyQuotes(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/quotes?${query}`);
    },

    async listCompanyCarriers(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/carriers?${query}`);
    },

    async listCompanyClients(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/clients?${query}`);
    },

    async listCompanyOrders(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/orders?${query}`);
    },

    async listCompanyAddresses(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<any>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<any>>(`/platform/organizations/${companyId}/addresses?${query}`);
    },

    async getReferenceData(): Promise<ApiSingleResponse<any>> {
      return fetcher<ApiSingleResponse<any>>("/platform/reference-data");
    },

    async getCompanyReferenceData(): Promise<ApiSingleResponse<any>> {
      return fetcher<ApiSingleResponse<any>>("/platform/reference-data/companies");
    },

    // ===================== TOP-LEVEL PLATFORM =====================
    async listUsers(filters: UserFilters = {}): Promise<ApiCollectionResponse<PlatformUser>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformUser>>(`/platform/admins?${query}`);
    },

    async getUserById(id: string): Promise<ApiSingleResponse<PlatformUser>> {
      return fetcher<ApiSingleResponse<PlatformUser>>(`/platform/admins/${id}`);
    },

    async createUser(payload: CreatePlatformUserDto): Promise<ApiSingleResponse<PlatformUser>> {
      return fetcher<ApiSingleResponse<PlatformUser>>("/platform/admins", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    async updateUser(id: string, payload: UpdatePlatformUserDto): Promise<ApiSingleResponse<PlatformUser>> {
      return fetcher<ApiSingleResponse<PlatformUser>>(`/platform/admins/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    },

    async listRoles(filters: RoleFilters = {}): Promise<ApiCollectionResponse<Role>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<Role>>(`/platform/roles?${query}`);
    },

    async listPermissions(filters: PermissionFilters = {}): Promise<ApiCollectionResponse<Permission>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<Permission>>(`/platform/permissions?${query}`);
    },

    async getStats(): Promise<ApiSingleResponse<DashboardStats>> {
      return fetcher<ApiSingleResponse<DashboardStats>>("/platform/stats/dashboard");
    },

    // ===================== BROADCASTS =====================
    async getBroadcastAudienceCounts(): Promise<ApiSingleResponse<BroadcastAudienceCounts>> {
      return fetcher<ApiSingleResponse<BroadcastAudienceCounts>>(
        "/platform/broadcasts/audience-counts",
      );
    },

    async listBroadcasts(): Promise<ApiSingleResponse<Broadcast[]>> {
      return fetcher<ApiSingleResponse<Broadcast[]>>("/platform/broadcasts");
    },

    async sendBroadcast(payload: SendBroadcastDto): Promise<ApiSingleResponse<Broadcast>> {
      return fetcher<ApiSingleResponse<Broadcast>>("/platform/broadcasts", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },

    async deleteBroadcast(id: string): Promise<void> {
      await fetcher(`/platform/broadcasts/${id}`, { method: "DELETE" });
    },

    // ===================== CROSS-TENANT READS =====================
    async listShipments(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformShipment>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformShipment>>(`/platform/organizations/${companyId}/shipments?${query}`);
    },

    async listDrivers(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformDriver>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformDriver>>(`/platform/organizations/${companyId}/drivers?${query}`);
    },

    async listVehicles(companyId: string, filters: any = {}): Promise<ApiCollectionResponse<PlatformVehicle>> {
      const query = buildParams(filters);
      return fetcher<ApiCollectionResponse<PlatformVehicle>>(`/platform/organizations/${companyId}/vehicles?${query}`);
    },

    // ===================== DEVICE SESSIONS =====================
    /** A tenant user's active sessions, for the admin device list. */
    async listUserSessions(userId: string): Promise<SessionDTO[]> {
      const res = await fetcher<ApiSingleResponse<SessionDTO[]>>(
        `/platform/tenant-users/${userId}/sessions`,
      );
      return res.data ?? [];
    },

    /** Force-revoke any session by id (kicks the device on next request). */
    async revokeSession(id: string): Promise<void> {
      await fetcher(`/platform/sessions/${id}`, { method: "DELETE" });
    },

    /** Every active session across an organization's users. */
    async listCompanySessions(companyId: string): Promise<CompanySessionDTO[]> {
      const res = await fetcher<ApiSingleResponse<CompanySessionDTO[]>>(
        `/platform/organizations/${companyId}/sessions`,
      );
      return res.data ?? [];
    },
  };
}
